import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RateServiceCode } from "@redotech/redo-fulfillment-rate-common/rate-service-snapshot";
import {
  ShoppedService,
  ShoppedServiceAccountDisambiguator,
  ShoppedServiceAccountDisambiguatorKind,
  ShoppedServiceAccountKind,
  ShoppedServiceAvailability,
  ShoppedServiceGroup,
  ShoppedServiceReason,
  ShoppedServicesNoticeKind,
} from "@redotech/redo-fulfillment-rate-common/shopped-services";
import { t } from "@redotech/redo-i18n";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";

function rateServiceCodeText(code: RateServiceCode): string {
  switch (code) {
    case RateServiceCode.RedoFedexRestricted:
      return t(
        "Redo's FedEx account only offers FedEx 2Day and international services.",
      );
    case RateServiceCode.SaturdayDeliveryRequired:
      return t("This service can't deliver by Saturday.");
    case RateServiceCode.SupersededByDirectUsps:
      return t("Shown from your own USPS account instead.");
    case RateServiceCode.Deduplicated:
      return t("This rate was removed as a duplicate.");
    case RateServiceCode.PoBoxNotSupported:
      return t("Delivery address is a PO box.");
    case RateServiceCode.ApoNotSupported:
      return t(
        "This service doesn't deliver to military (APO/FPO/DPO) addresses.",
      );
    case RateServiceCode.SmartpostDryIce:
      return t("This service can't ship dry ice.");
    case RateServiceCode.HazmatNotSupported:
      return t("This service can't ship hazardous materials.");
    case RateServiceCode.NearZeroProviderCost:
    case RateServiceCode.InvalidProviderRate:
      return t("The carrier returned an invalid price for this service.");
    case RateServiceCode.IncotermNotSupported:
      return t(
        "This service doesn't support the shipment's duties and taxes terms.",
      );
    case RateServiceCode.UnexpectedCarrier:
      return t(
        "This rate came from a carrier Redo didn't expect, so it was removed.",
      );
    case RateServiceCode.CarrierAccountBlocked:
      return t("This Redo carrier account is turned off for your store.");
    case RateServiceCode.TiktokUspsNotSupported:
      return t("TikTok orders can't ship with this USPS service.");
    case RateServiceCode.WalmartAmazonNotSupported:
      return t("Walmart orders can't ship with Amazon Shipping.");
    case RateServiceCode.LandedCostNotSupported:
      return t("This service doesn't support prepaid duties and taxes.");
    case RateServiceCode.LandedCostNotGuaranteed:
      return t("Duties and taxes couldn't be guaranteed for this service.");
    case RateServiceCode.DisabledInSettings:
      return t("Turned off in your carrier settings.");
    case RateServiceCode.BlockedByRedo:
      return t("Redo has turned off this service for your store.");
    case RateServiceCode.RateSourceFailed:
      return t("Redo couldn't get rates from this carrier account.");
    case RateServiceCode.CarrierTimeout:
      return t(
        "The carrier didn't respond in time. Get updated rates to try again.",
      );
    case RateServiceCode.CarrierMessage:
      return t("The carrier returned an error for this service.");
    case RateServiceCode.NoHsCodes:
      return t("International shipments need an HS code on every item.");
    case RateServiceCode.BadAddress:
      return t("The shipping address couldn't be verified.");
    case RateServiceCode.MultiParcelHazmat:
      return t("Hazardous materials can't ship in multi-package shipments.");
    case RateServiceCode.EasypostRateLimited:
      return t("Too many rate requests. Get updated rates in a minute.");
    case RateServiceCode.RatingFailed:
      return t("Rates couldn't be loaded. Get updated rates to try again.");
    case RateServiceCode.FirstClassLettersOnly:
      return t(
        "First-Class Mail is only for USPS Letter, Card and Flat packages.",
      );
    case RateServiceCode.MediaMailNotEligible:
      return t(
        "To get Media Mail, mark the shipment USPS Media Mail eligible in Additional settings.",
      );
    case RateServiceCode.LibraryMailNotEligible:
      return t(
        "To get Library Mail, mark the shipment USPS Library Mail eligible in Additional settings.",
      );
    case RateServiceCode.FreightBelowMinimumWeight:
      return t("Freight services are only for shipments over 150 lb.");
    case RateServiceCode.BusinessAddressOnly:
      return t(
        "This service only delivers to business addresses, and this address is marked residential.",
      );
    case RateServiceCode.RatedAsFedexHomeDelivery:
      return t("FedEx rates residential addresses as FedEx Home Delivery.");
    case RateServiceCode.RatedAsFedexGround:
      return t("FedEx rates business addresses as FedEx Ground.");
    default:
      return assertNever(code);
  }
}

/** The carrier's own message when it gave one, else what the code means. */
export function shoppedServiceReasonText(reason: ShoppedServiceReason): string {
  if (reason.message) {
    return reason.message;
  }
  return reason.code
    ? rateServiceCodeText(reason.code)
    : t("The carrier didn't say why this service is unavailable.");
}

/** The group whose shopped services the Service field links to: only a single group the team can edit. */
export function allShoppedServicesGroupId(
  fulfillmentGroups: readonly Pick<FulfillmentGroupData, "_id">[],
  everythingDisabled: boolean,
): string | undefined {
  return fulfillmentGroups.length === 1 && !everythingDisabled
    ? fulfillmentGroups[0]?._id.toString()
    : undefined;
}

function disambiguatorSuffix(
  value: ShoppedServiceAccountDisambiguator | null,
): string {
  if (!value) {
    return "";
  }
  switch (value.kind) {
    case ShoppedServiceAccountDisambiguatorKind.Last4:
      return ` ••••${value.last4}`;
    case ShoppedServiceAccountDisambiguatorKind.Ordinal:
      return ` ${t("(account {{ordinal}})", { ordinal: value.ordinal })}`;
    default:
      return assertNever(value);
  }
}

export function shoppedServiceGroupLabel(
  group: ShoppedServiceGroup,
  viewerIsFulfillmentService: boolean,
): string {
  const carrier = carrierToLabel[group.carrier];
  switch (group.accountKind) {
    case ShoppedServiceAccountKind.Redo:
      return t("{{carrier}} · Redo account", { carrier });
    case ShoppedServiceAccountKind.Merchant: {
      const suffix = disambiguatorSuffix(group.accountDisambiguator);
      if (group.accountName) {
        return `${carrier} · ${group.accountName}${suffix}`;
      }
      return (
        (viewerIsFulfillmentService
          ? t("{{carrier}} · Merchant account", { carrier })
          : t("{{carrier}} · Your account", { carrier })) + suffix
      );
    }
    default:
      return assertNever(group.accountKind);
  }
}

export function filterShoppedServiceGroups(
  groups: readonly ShoppedServiceGroup[],
  query: string,
  viewerIsFulfillmentService: boolean,
): ShoppedServiceGroup[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return [...groups];
  }
  const entries = groups.flatMap((group) =>
    group.services.map((service) => ({
      group,
      service,
      searchText: `${shoppedServiceGroupLabel(group, viewerIsFulfillmentService)} ${getCarrierServiceLabel(service.carrier, service.service)}`,
    })),
  );
  const matches = new Set(
    // ignoreLocation: the carrier and account prefix would otherwise push service names past Fuse's match distance.
    new Fuse(entries, {
      keys: ["searchText"],
      threshold: 0.3,
      ignoreLocation: true,
    })
      .search(trimmed)
      .map((result) => result.item.service),
  );
  return groups
    .map((group) => ({
      ...group,
      services: group.services.filter((service) => matches.has(service)),
    }))
    .filter((group) => group.services.length > 0);
}

/** Turned off in the merchant's own carrier settings, so they can turn it back on. */
export function isTurnedOffInSettings(service: ShoppedService): boolean {
  return (
    service.availability === ShoppedServiceAvailability.Unavailable &&
    service.reason.code === RateServiceCode.DisabledInSettings
  );
}

function unavailableCounts(groups: readonly ShoppedServiceGroup[]): string[] {
  const unavailable = groups
    .flatMap((group) => group.services)
    .filter(
      (service) =>
        service.availability === ShoppedServiceAvailability.Unavailable,
    );
  const turnedOff = unavailable.filter(isTurnedOffInSettings).length;
  return [
    t("{{count}} unavailable", { count: unavailable.length - turnedOff }),
    ...(turnedOff > 0 ? [t("{{count}} turned off", { count: turnedOff })] : []),
  ];
}

export function shoppedServicesSummary(
  allGroups: readonly ShoppedServiceGroup[],
  visibleGroups: readonly ShoppedServiceGroup[],
  query: string,
): string {
  const trimmed = query.trim();
  const visibleServices = visibleGroups.flatMap(
    (group) => group.services,
  ).length;
  if (!trimmed) {
    const carriers = new Set(
      allGroups.map((group) => carrierToLabel[group.carrier]),
    ).size;
    return [
      t("{{count}} service(s)", { count: visibleServices }),
      t("{{count}} carrier(s)", { count: carriers }),
      ...unavailableCounts(allGroups),
    ].join(" · ");
  }
  if (visibleServices === 0) {
    return t("No services match “{{query}}”", { query: trimmed });
  }
  return [
    t("{{count}} service(s) matching “{{query}}”", {
      count: visibleServices,
      query: trimmed,
    }),
    ...unavailableCounts(visibleGroups),
  ].join(" · ");
}

function nothingRatedSubtext(notice: ShoppedServicesNoticeKind): string {
  switch (notice) {
    case ShoppedServicesNoticeKind.Precheck:
      return t("Fix the issue above, then get updated rates.");
    case ShoppedServicesNoticeKind.None:
    case ShoppedServicesNoticeKind.NoSnapshot:
    case ShoppedServicesNoticeKind.RatesPending:
      return t("Get updated rates to try again.");
    default:
      return assertNever(notice);
  }
}

export function shoppedServicesEmptyState(
  allGroups: readonly ShoppedServiceGroup[],
  visibleGroups: readonly ShoppedServiceGroup[],
  notice: ShoppedServicesNoticeKind,
): { text: string; subtext: string } | null {
  if (allGroups.length === 0) {
    return {
      text: t("No services were rated."),
      subtext: nothingRatedSubtext(notice),
    };
  }
  if (visibleGroups.length === 0) {
    return {
      text: t("No services found"),
      subtext: t("Try another carrier or service name."),
    };
  }
  return null;
}
